import getVideoAdTagUrl from 'Utils/video-ad-tag-url';
import getWindow from 'Utils/get-window';

// IMA SDK error code raised when the ad request to pubads.g.doubleclick.net
// itself fails (AdError.ErrorCode.FAILED_TO_REQUEST_ADS). In our telemetry this
// is a per-client, all-or-nothing condition (sessions that hit it essentially
// never see any other ad outcome), i.e. the request is being blocked on the
// client — typically an ad blocker whose list allows imasdk.googleapis.com (so
// the SDK-missing gate doesn't fire) but blocks the gampad/ads request.
export const IMA_FAILED_TO_REQUEST_ADS = 1005;

// Pull the VAST + IMA codes, message and the SDK's inner error out of a
// videojs-ima `adserror` event. `context` (e.g. { probe: 'reachable',
// online: 1 }) is appended as " k=v" pairs so a single event_url line carries
// everything needed to split a code by cause. Defensive: this runs in the
// player's error path, so it must never throw.
const serializeContext = (context) => {
  try {
    return Object.keys(context || {})
      .filter((k) => context[k] !== undefined && context[k] !== null)
      .map((k) => ` ${k}=${String(context[k]).replace(/\s+/g, '_')}`)
      .join('');
  } catch (e) {
    return '';
  }
};

export const getAdsErrorDetail = (e, context = {}) => {
  try {
    const adError = e && e.data && e.data.AdError;
    const vastCode =
      adError && adError.getVastErrorCode ? adError.getVastErrorCode() : '';
    const errCode =
      adError && adError.getErrorCode ? adError.getErrorCode() : '';
    const message =
      (adError && adError.getMessage && adError.getMessage()) ||
      (e && e.message) ||
      '';
    // IMA wraps the underlying failure (network error, blocked frame, parse
    // failure) in getInnerError(); for 1005 this is the only place the real
    // cause surfaces.
    let innerMessage = '';
    try {
      const inner =
        adError && adError.getInnerError ? adError.getInnerError() : null;
      innerMessage = inner
        ? (inner.getMessage ? inner.getMessage() : inner.message) || ''
        : '';
    } catch (innerError) {
      innerMessage = '';
    }
    const contextStr = serializeContext(context);
    // event_url: "vast=<code> code=<code> <message>[ k=v ...][ inner=<inner>]"
    // — the "vast= code=" prefix is stable so existing LIKE queries keep
    // working, fixed-shape context (no spaces) comes next, and the free-text
    // inner message is LAST so `inner=` runs to end of line unambiguously.
    const url = `vast=${vastCode} code=${errCode} ${message}${contextStr}${
      innerMessage ? ` inner=${innerMessage}` : ''
    }`.slice(0, 480);
    return { vastCode, errCode, message, innerMessage, url };
  } catch (extractionError) {
    // Even when the IMA accessors throw, keep the caller's context (probe,
    // online, t) on the line so every ad_error stays timestamped/attributed.
    return {
      vastCode: '',
      errCode: '',
      message: '',
      innerMessage: '',
      url: `vast= code= (error-detail extraction failed)${serializeContext(
        context,
      )}`.slice(0, 480),
    };
  }
};

// 1 / 0 from navigator.onLine, '?' when unavailable (SSR, old browsers).
export const getOnlineFlag = () => {
  try {
    const { navigator } = getWindow();
    if (!navigator || typeof navigator.onLine !== 'boolean') {
      return '?';
    }
    return navigator.onLine ? 1 : 0;
  } catch (e) {
    return '?';
  }
};

// True when the adserror is IMA's generic FAILED_TO_REQUEST_ADS. This alone is
// not proof of a blocker (a malformed tag or an ordinary network/CORS failure
// raises it too) — pair it with `probeAdServerReachable` before gating.
export const isAdRequestFailedError = (e) =>
  Number(getAdsErrorDetail(e).errCode) === IMA_FAILED_TO_REQUEST_ADS;

// Host IMA sends the ad request to. Ad-blocker lists block this whole host,
// while some still allow imasdk.googleapis.com (so the SDK loads and the
// missing-SDK gate never fires). A deliberately invalid, no-cors request here
// costs GAM nothing (no ad unit -> rejected, not counted) but tells us whether
// the host is reachable at all from this client.
export const AD_SERVER_PROBE_URL =
  'https://pubads.g.doubleclick.net/gampad/ads?output=vast&ftv_probe=1';
export const AD_SERVER_PROBE_TIMEOUT_MS = 1500;

// Independent blocker signal with a distinct outcome per case, so telemetry
// can tell "the host answered" from "we could not tell":
//   'reachable'   — the no-cors fetch resolved (any HTTP status = opaque ok)
//   'unreachable' — the request itself failed (blocked by the client)
//   'timeout'     — no answer within timeoutMs (slow network; inconclusive)
//   'unavailable' — no usable fetch (SSR / old browser / sync throw)
// Only 'unreachable' is evidence of a blocker; callers gate on that alone.
export const PROBE = {
  REACHABLE: 'reachable',
  UNREACHABLE: 'unreachable',
  TIMEOUT: 'timeout',
  UNAVAILABLE: 'unavailable',
};
export const probeAdServer = ({
  fetch = getWindow().fetch,
  timeoutMs = AD_SERVER_PROBE_TIMEOUT_MS,
} = {}) => {
  if (typeof fetch !== 'function') {
    return Promise.resolve(PROBE.UNAVAILABLE);
  }
  // Abort the underlying request when the probe times out, so a stalled
  // client does not accumulate a hanging ad-host request per attempt. The
  // constructor comes off the window (absent in SSR / old browsers -> no abort).
  const { AbortController: Aborter } = getWindow();
  const controller = typeof Aborter === 'function' ? new Aborter() : null;
  let attempt;
  try {
    attempt = fetch(AD_SERVER_PROBE_URL, {
      mode: 'no-cors',
      cache: 'no-store',
      credentials: 'omit',
      ...(controller && { signal: controller.signal }),
    }).then(
      () => PROBE.REACHABLE,
      () => PROBE.UNREACHABLE,
    );
  } catch (e) {
    // A synchronous throw is a bad call, not a blocked request.
    return Promise.resolve(PROBE.UNAVAILABLE);
  }
  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => {
      if (controller) {
        try {
          controller.abort();
        } catch (e) {
          // ignore
        }
      }
      resolve(PROBE.TIMEOUT);
    }, timeoutMs);
  });
  return Promise.race([attempt, timeout]).then((status) => {
    clearTimeout(timer);
    return status;
  });
};

// Boolean view: anything other than a confirmed 'unreachable' fails open.
export const probeAdServerReachable = (opts) =>
  probeAdServer(opts).then((status) => status !== PROBE.UNREACHABLE);

// Rewrite the autoplay signal on an already-built tag (the embedded player
// builds its override tag before the player knows whether the browser will
// actually allow autoplay), so `vpa` always matches IMA adWillAutoPlay and the
// playback path.
export const withAutoplaySignal = (tagUrl, willAutoPlay) => {
  if (!tagUrl) {
    return tagUrl;
  }
  try {
    const u = new URL(tagUrl);
    u.searchParams.set('vpa', willAutoPlay ? 'auto' : 'click');
    return u.toString();
  } catch (e) {
    return tagUrl;
  }
};

// Build the pre-roll ad tag for the on-site (non-embedded) player. Mirrors the
// embeddable player's tag: the item's custom targeting (video_duration,
// section_id, topics, publisher_id) goes into cust_params, and the GAM video
// signals programmatic buyers require are set explicitly:
//   description_url — the page the video plays on. Required for AdX and Open
//     Bidding video demand to bid at all. (`url` is set by the IMA SDK itself.)
//   plcmt=1 — in-stream (pre-roll) placement classification.
//   vpa / vpmute — autoplay + mute context.
// `pageUrl` defaults to the current location (client only).
export const buildPrerollAdTagUrl = (
  videoAdOptions = {},
  { autoPlay = false, muted = false, pageUrl } = {},
) => {
  const href =
    pageUrl !== undefined
      ? pageUrl
      : (getWindow().location && getWindow().location.href) || undefined;
  return getVideoAdTagUrl({
    ...videoAdOptions,
    vpa: autoPlay ? 'auto' : 'click',
    vpmute: muted ? 1 : 0,
    plcmt: 1,
    ...(href && { description_url: href }),
  });
};
